import React, { useRef, useState } from "react";
import { Popper, PopperProps } from "@material-ui/core";
import { createObjectID } from "@bonliva-crm/common";
import { hasIdInParent } from "../utils/dom";
import { useOnClickOutside } from "../utils/use-on-click-outside";

type PopperContentProps = {
  open: boolean;
  close: () => void;
};

type Props = {
  /**
   * Behaviour of the popper. Default is "click".
   */
  behaviour?: "click" | "hover";
  popperContent: React.ReactNode | ((props: PopperContentProps) => React.ReactNode);
  children: React.ReactNode | ((props: PopperContentProps) => React.ReactNode);
  PopperProps?: Partial<PopperProps>;
  placement?: PopperProps["placement"];
  closeOnClickInside?: boolean;
  rootStyle?: React.CSSProperties;
};

export const WithPopper = (props: Props) => {
  const behaviour = props.behaviour || "click";

  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [id] = useState(createObjectID());

  useOnClickOutside(ref, (e) => {
    const shouldClose = props.closeOnClickInside ? true : !hasIdInParent(e.target, id);

    if (shouldClose) {
      setTimeout(() => setOpen(false), 100);
    }
  });

  const popperProps: Partial<PopperProps> = {};

  if (props.placement) {
    popperProps.placement = props.placement;
  }

  if (props.PopperProps) {
    Object.assign(popperProps, props.PopperProps);
  }

  return (
    <div
      ref={ref}
      onClick={(e) => {
        if (behaviour === "click") {
          e.stopPropagation();
          setOpen(true);
        }
      }}
      onMouseOver={(e) => {
        if (behaviour === "hover") {
          e.stopPropagation();
          setOpen(true);
        }
      }}
      onMouseLeave={(e) => {
        if (behaviour === "hover") {
          e.stopPropagation();
          setOpen(false);
        }
      }}
      style={{ cursor: "pointer", ...props.rootStyle }}
    >
      {typeof props.children === "function"
        ? props.children({ open, close: () => setOpen(false) })
        : props.children}

      <Popper
        id={id}
        open={open}
        anchorEl={ref.current}
        style={{ zIndex: 10001 }}
        {...popperProps}
      >
        {typeof props.popperContent === "function"
          ? props.popperContent({ open, close: () => setOpen(false) })
          : props.popperContent}
      </Popper>
    </div>
  );
};
